import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { getCurrencySymbol } from "./currencies";
import AddNotesFrais from "./AddNotesFrais";
import {
    getExpenseNotesToValidate,
    validateExpenseNote,
    refuseExpenseNote,
} from "../../../_api/ExpenseNote";

const STATUS_LABELS = {
    PENDING: "En attente",
    ACCEPTED: "Validée",
    REFUSED: "Refusée",
};

const getStatutStyle = (status) => {
    switch (status) {
        case "ACCEPTED":
            return "text-green-600 bg-green-100";
        case "PENDING":
            return "text-yellow-600 bg-yellow-100";
        case "REFUSED":
            return "text-red-600 bg-red-100";
        default:
            return "text-gray-600 bg-gray-100";
    }
};

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
    return d.toISOString().slice(0, 10);
};

const getUserName = (user) => {
    if (!user) return "-";
    return [user.first_name, user.last_name].filter(Boolean).join(" ") || "-";
};

const GestionNotesFrais = () => {
    const [selectedNote, setSelectedNote] = useState(null);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchNotes = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getExpenseNotesToValidate();
            setNotes(res.data || res);
        } catch (err) {
            setError(err?.response?.data?.message || "Erreur lors du chargement.");
            setNotes([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotes();
    }, []);

    const handleValidate = async (id) => {
        try {
            await validateExpenseNote(id);
            toast.success("Note de frais validée.");
            setSelectedNote(null);
            fetchNotes();
        } catch (err) {
            const msg =
                err?.response?.data?.message || "Erreur lors de la validation.";
            toast.error(msg);
            throw err;
        }
    };

    const handleRefuse = async (id, comment = null) => {
        try {
            await refuseExpenseNote(id, comment);
            toast.success("Note de frais refusée.");
            setSelectedNote(null);
            fetchNotes();
        } catch (err) {
            const msg =
                err?.response?.data?.message || "Erreur lors du refus.";
            toast.error(msg);
            throw err;
        }
    };

    const displayNotes = notes.map((n) => ({
        ...n,
        date: formatDate(n.date),
        type: n.expense_type,
        montant: parseFloat(n.amount) || 0,
        currency: n.currency || "EUR",
        statutLabel: STATUS_LABELS[n.status] || n.status,
        commentaire: n.comment,
        utilisateur: getUserName(n.user),
    }));

    return (
        <div className="relative p-4">
            <h2 className="text-xl font-bold mb-4">
                Validation des notes de frais
            </h2>

            {loading && (
                <div className="text-gray-500 text-sm py-4">Chargement...</div>
            )}
            {error && (
                <div className="text-red-600 text-sm py-4">{error}</div>
            )}
            {!loading && !error && (
                <>
                    <table className="w-full table-auto border border-gray-200 text-sm">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="p-2">Utilisateur</th>
                                <th className="p-2">Date</th>
                                <th className="p-2">Type</th>
                                <th className="p-2">Montant</th>
                                <th className="p-2">Statut</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayNotes.map((note) => (
                                <tr
                                    key={note.id}
                                    className="border-t hover:bg-gray-50 cursor-pointer"
                                    onClick={() => setSelectedNote(note)}
                                >
                                    <td className="p-2">{note.utilisateur}</td>
                                    <td className="p-2">{note.date}</td>
                                    <td className="p-2">{note.type}</td>
                                    <td className="p-2">
                                        {note.montant.toFixed(2)} {getCurrencySymbol(note.currency)}
                                    </td>
                                    <td className="p-2">
                                        <span
                                            className={`px-2 py-1 rounded-full ${getStatutStyle(
                                                note.status
                                            )}`}
                                        >
                                            {note.statutLabel}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {displayNotes.length === 0 && (
                        <div className="text-gray-500 text-sm py-8 text-center">
                            Aucune note en attente de validation.
                        </div>
                    )}
                </>
            )}

            {selectedNote && (
                <div className="fixed top-0 right-0 w-full sm:w-96 h-full z-50 bg-white shadow-xl border-l">
                    <AddNotesFrais
                        open={!!selectedNote}
                        onClose={() => setSelectedNote(null)}
                        note={selectedNote}
                        editable={false}
                        onValidate={handleValidate}
                        onRefuse={handleRefuse}
                    />
                </div>
            )}
        </div>
    );
};

export default GestionNotesFrais;
